import React, { useEffect, useState } from 'react';

import { DatasetDataApi } from "api/DatasetDataApi";
import { useGetDatasetDataInfo } from "api/api-hooks/datasets-hooks";
import { DatasetDataLagLeadRequest, UpdateDatasetData } from "api/model/requests";
import ChartInDialogButton from "components/basic/chart-in-dialog-button/ChartInDialogButton";
import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { T } from 'services/lang/LanguageService';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Message } from "primereact/message";
import { ProgressSpinner } from 'primereact/progressspinner';
import { ChronoUnitLegacy } from "utils/time-utils";
import {
    BooleanTypeTemplate,
    NumberTypeTemplate,
    TitleOverrideAndDescriptionTemplate
} from "../../../templates/DataTableCellTemplates";
import { ICombinedData, ITableConfigProp } from '../common/api/interfaces/interfaces';
import DataInputOutputUpdateTable from "../update-dataset-table/DataInputOutputUpdateTable";
import {
    getDataConfigLocalFullyFilledConfig,
    getDateFromGivenConfig,
    getExtractedDataValue,
    remakeArrayIntoOtherInterface
} from '../utils';
import './DataInputOutputTable.scss';

const DataInputOutputTable = (props: ITableConfigProp) => {
    const identifiers = props.datasets.map(ds => ds.identifier)

    const config: DatasetDataLagLeadRequest = getDataConfigLocalFullyFilledConfig(props.config || {});
    const [dataQuery, setDataQuery] = useState<Array<ICombinedData>>([])

    const [dialog, setDialog] = useState(false);
    const dialogFuncMap: any = {'dialog': setDialog}

    const {data, isError, isLoading, refetch} = useGetDatasetDataInfo(identifiers, config, {
        enabled: config && identifiers && identifiers.length > 0,
        refetchInterval: props.config?.updateFrequency && !dialog ? props.config?.updateFrequency * 1000 : undefined,
    })

    useEffect(() => {
        if (data && !isError && !isLoading && data?.length > 0) {
            setDataQuery([...remakeArrayIntoOtherInterface(data, props.datasets)])
        }
    }, [data])

    const onClick = (name: string) => {
        dialogFuncMap[`${name}`](true);
    }

    const onHide = (name: string) => {
        refetch();
        dialogFuncMap[`${name}`](false);
    }

    const titleColumnRender = (rowData: ICombinedData) => {
        return <div className="flex gap-2">
            {TitleOverrideAndDescriptionTemplate({
                title: rowData.title,
                overriddenTitle: rowData.overriddenTitle,
                description: rowData.description,
                identifier: rowData.identifier,
            })}
            <ChartInDialogButton identifier={rowData.identifier}/>
        </div>
    }

    const valueColumnRender = (rowData: ICombinedData) => {
      if (rowData.type.toUpperCase() === "NUMBER") {
        return NumberTypeTemplate(rowData.data, rowData.unit, rowData.decimalPlaces);
      }
      if (rowData.type.toUpperCase() === "BOOLEAN") {
        const changeBooleanValue = (val: boolean) => {
          const boolToNumber = val ? 1 : 0;

          DatasetDataApi.updateData([
            {
              identifier: rowData.identifier,
              values: {
                [`${rowData.data[0].key}`]: boolToNumber,
              },
            },
          ] as Array<UpdateDatasetData>)
            .then(() => {
              easasAlert({ summary: T("alert.datasets-updated"), severity: "success" });
              setDataQuery(oldDataQuery => {
                return oldDataQuery.map(ob => {
                  if (ob.identifier === rowData.identifier) {
                    ob.data[0].value = boolToNumber;
                  }
                  return ob;
                });
              });
            })
            .catch(er => {
              console.error(er);
              easasAlert({ summary: T("alert.error-while-updating-datasets"), severity: "error" });
            });
        };

        return BooleanTypeTemplate(rowData.data, changeBooleanValue);
      }
      if (rowData.type.toUpperCase() === "ENUM") {
        let val = "";
        if (rowData.data && rowData.data.length > 0 && rowData.enum) {
          Object.entries(rowData.enum).forEach(([key, value]) => {
            if (value === +getExtractedDataValue(rowData.data)) {
              val = key;
            }
          });
        }
        return val || "-";
      }
      return T("message.unknown-type");
    };

    return (
      <>
        {isError && (
          <div className="error-window">
            <Message severity="error" text={T("error.error-while-fetching-datasets")} />
          </div>
        )}

        {isLoading && !isError && (
          <ProgressSpinner
            style={{ display: "flex", justifyContent: "space-around", overflow: "hidden" }}
          />
        )}

        {data && !isError && (
          <div className="full-table">
            <DataTable
              width="100%"
              value={dataQuery}
              responsiveLayout="scroll"
              scrollHeight={`${props.config?.maxHeight}px`}
              size="small"
            >
              <Column
                field="title"
                header={T("word.dataset")}
                body={titleColumnRender}
                style={{ paddingLeft: "15px" }}
              />
              <Column
                field="unit"
                header={T("word.value")}
                body={valueColumnRender}
                style={{
                  width: "10px",
                  paddingRight: "15px",
                  whiteSpace: "nowrap",
                }}
              />
            </DataTable>

            {data && data.length > 0 && (
              <Button
                style={{ margin: "0.3vw" }}
                label={T("action.update")}
                className="p-button-success p-button-rounded p-button-sm"
                icon="pi pi-pencil"
                onClick={() => onClick("dialog")}
              />
            )}

            <Dialog
              breakpoints={{ "960px": "75vw", "640px": "100vw" }}
              style={{ width: "50vw" }}
              draggable={false}
              resizable={false}
              header={T("action.update-datasets")}
              visible={dialog}
              onHide={() => onHide("dialog")}
              blockScroll
            >
              <DataInputOutputUpdateTable
                titleColumnReader={titleColumnRender}
                identifiers={identifiers}
                firstLoadDate={getDateFromGivenConfig(props.config || {})}
                datasets={props.datasets}
                dateFrequency={
                  (props.config?.dataFrequency as ChronoUnitLegacy) || ChronoUnitLegacy.MINUTES
                }
                closeDialog={() => onHide("dialog")}
              />
            </Dialog>
          </div>
        )}
      </>
    );
}

export default DataInputOutputTable;